/**
 * DevExtreme (viz/components/chart_theme_manager.js)
 * Version: 16.2.5
 * Build date: Mon Feb 27 2017
 *
 * Copyright (c) 2012 - 2017 Developer Express Inc. ALL RIGHTS RESERVED
 * EULA: https://www.devexpress.com/Support/EULAs/DevExtreme.xml
 */
"use strict";
var $ = require("jquery"),
    commonUtils = require("../../core/utils/common"),
    BaseThemeManager = require("../core/base_theme_manager").BaseThemeManager,
    _isString = commonUtils.isString,
    _isDefined = commonUtils.isDefined,
    _normalizeEnum = require("../core/utils").normalizeEnum,
    FONT = "font",
    COMMON_AXIS_SETTINGS = "commonAxisSettings",
    PIE_FONT_FIELDS = ["legend." + FONT, "title." + FONT, "title.subtitle." + FONT, "tooltip." + FONT, "loadingIndicator." + FONT, "export." + FONT, "commonSeriesSettings.label." + FONT],
    POLAR_FONT_FIELDS = PIE_FONT_FIELDS.concat([COMMON_AXIS_SETTINGS + ".label." + FONT, COMMON_AXIS_SETTINGS + ".title." + FONT]),
    CHART_FONT_FIELDS = POLAR_FONT_FIELDS.concat(["crosshair.label." + FONT]),
    chartToFontFieldsMap = {
        pie: PIE_FONT_FIELDS,
        chart: CHART_FONT_FIELDS,
        polar: POLAR_FONT_FIELDS
    };
var ThemeManager = BaseThemeManager.inherit(function() {
    var ctor = function(options, themeGroupName) {
        var that = this;
        that.callBase.apply(that, arguments);
        options = options || {};
        that._userOptions = options;
        that._mergeAxisTitleOptions = [];
        that._multiPieColors = {};
        that._themeSection = themeGroupName;
        that._fontFields = chartToFontFieldsMap[themeGroupName];
        that._callback = $.noop
    };
    var dispose = function() {
        var that = this;
        that.palette && that.palette.dispose();
        that.palette = that._userOptions = that._mergedSettings = that._multiPieColors = null;
        return that.callBase.apply(that, arguments)
    };
    var resetPalette = function() {
        this.palette.reset();
        this._multiPieColors = {}
    };
    var updatePalette = function(palette) {
        this.palette = this.createPalette(palette, {
            useHighlight: true
        })
    };
    var processTitleOptions = function(options) {
        return _isString(options) ? {
            text: options
        } : options
    };
    var processAxisOptions = function(axisOptions) {
        if (!axisOptions) {
            return
        }
        axisOptions = $.extend(true, {}, axisOptions);
        axisOptions.title = processTitleOptions(axisOptions.title);
        if ("logarithmic" === axisOptions.type && axisOptions.logarithmBase <= 0 || axisOptions.logarithmBase && !$.isNumeric(axisOptions.logarithmBase)) {
            axisOptions.logarithmBase = void 0;
            axisOptions.logarithmBaseError = true
        }
        if (axisOptions.label) {
            if (axisOptions.label.alignment) {
                axisOptions.label.userAlignment = true
            }
            if (_isString(axisOptions.label.overlappingBehavior)) {
                axisOptions.label.overlappingBehavior = {
                    mode: axisOptions.label.overlappingBehavior
                }
            }
            if (!axisOptions.label.overlappingBehavior || !axisOptions.label.overlappingBehavior.mode) {
                axisOptions.label.overlappingBehavior = axisOptions.label.overlappingBehavior || {}
            }
        }
        return axisOptions
    };
    var applyParticularAxisOptions = function(name, userOptions, rotated) {
        var theme = this._theme,
            position = !(rotated ^ "valueAxis" === name) ? "horizontalAxis" : "verticalAxis",
            commonAxisSettings = processAxisOptions(this._userOptions.commonAxisSettings, name);
        return $.extend(true, {}, theme.commonAxisSettings, theme[position], theme[name], commonAxisSettings, processAxisOptions(userOptions, name))
    };
    var mergeOptions = function(name, userOptions) {
        userOptions = userOptions || this._userOptions[name];
        var theme = this._theme[name],
            result = this._mergedSettings[name];
        if (result) {
            return result
        }
        if ($.isPlainObject(theme) && $.isPlainObject(userOptions)) {
            result = $.extend(true, {}, theme, userOptions)
        } else {
            result = _isDefined(userOptions) ? userOptions : theme
        }
        this._mergedSettings[name] = result;
        return result
    };
    var applyParticularTheme = {
        base: mergeOptions,
        argumentAxis: applyParticularAxisOptions,
        valueAxisRangeSelector: function() {
            return mergeOptions.call(this, "valueAxis")
        },
        valueAxis: applyParticularAxisOptions,
        series: function(name, userOptions) {
            var settings, mainSeriesColor, seriesVisibility, that = this,
                theme = that._theme,
                userCommonSettings = that._userOptions.commonSeriesSettings || {},
                themeCommonSettings = theme.commonSeriesSettings,
                widgetType = that._themeSection.split(".").slice(-1)[0],
                type = _normalizeEnum(userOptions.type || userCommonSettings.type || themeCommonSettings.type || "pie" === widgetType && theme.type),
                palette = that.palette,
                isBar = ~type.indexOf("bar"),
                isLine = ~type.indexOf("line"),
                isArea = ~type.indexOf("area"),
                isBubble = "bubble" === type,
                resolveLabelsOverlapping = that.getOptions("resolveLabelsOverlapping"),
                resolveLabelOverlapping = that.getOptions("resolveLabelOverlapping"),
                containerBackgroundColor = that.getOptions("containerBackgroundColor");
            if (isBar || isBubble) {
                userOptions = $.extend(true, {}, userCommonSettings, userCommonSettings[type], userOptions);
                seriesVisibility = userOptions.visible;
                userCommonSettings = {
                    type: {}
                };
                $.extend(true, userOptions, userOptions.point);
                userOptions.visible = seriesVisibility
            }
            settings = $.extend(true, {}, themeCommonSettings, themeCommonSettings[type], userCommonSettings, userCommonSettings[type], userOptions);
            settings.type = type;
            settings.widgetType = widgetType;
            settings.containerBackgroundColor = containerBackgroundColor;
            if ("pie" !== widgetType) {
                mainSeriesColor = settings.color || palette.getNextColor()
            } else {
                mainSeriesColor = function(argument, index) {
                    var cat = argument + index;
                    if (!that._multiPieColors[cat]) {
                        that._multiPieColors[cat] = palette.getNextColor()
                    }
                    return that._multiPieColors[cat]
                }
            }
            settings.mainSeriesColor = mainSeriesColor;
            settings.resolveLabelOverlapping = resolveLabelOverlapping;
            settings.resolveLabelsOverlapping = resolveLabelsOverlapping;
            if (settings.label && (isLine || isArea && "rangearea" !== type || "scatter" === type)) {
                settings.label.position = "outside"
            }
            return settings
        },
        animation: function(name) {
            var userOptions = this._userOptions[name];
            userOptions = $.isPlainObject(userOptions) ? userOptions : _isDefined(userOptions) ? {
                enabled: !!userOptions
            } : {};
            return mergeOptions.call(this, name, userOptions)
        }
    };
    return {
        _themeSection: "chart",
        ctor: ctor,
        dispose: dispose,
        resetPalette: resetPalette,
        getOptions: function(name) {
            return (applyParticularTheme[name] || applyParticularTheme.base).apply(this, arguments)
        },
        refresh: function() {
            this._mergedSettings = {};
            return this.callBase.apply(this, arguments)
        },
        _initializeTheme: function() {
            var that = this;
            that.callBase.apply(that, arguments);
            that.updatePalette(that.getOptions("palette"))
        },
        resetOptions: function(name) {
            this._mergedSettings[name] = null
        },
        update: function(options) {
            this._userOptions = options
        },
        updatePalette: updatePalette
    }
}());
exports.ThemeManager = ThemeManager;



//////////////////
// WEBPACK FOOTER
// /home/backend/NasheDelo/WebLombard/client/~/devextreme/viz/components/chart_theme_manager.js
// module id = ../../../../devextreme/viz/components/chart_theme_manager.js
// module chunks = 3